import React, { useMemo } from "react";
import { Chart } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Tooltip,
  Legend,
  ChartOptions,
  ChartData,
} from "chart.js";

// Registro único (apenas barras — Pareto sem linha de acumulado).
ChartJS.register(CategoryScale, LinearScale, BarElement, BarController, Tooltip, Legend);

export interface ParetoChartColors {
  bars: string[];
  text: string;
  grid: string;
}

export interface ParetoChartProps {
  labels: string[];
  /** Valores das barras na unidade de exibição (horas ou ocorrências). */
  barValues: number[];
  /** % que cada estado representa do total da dimensão (para o tooltip). */
  share: number[];
  colors: ParetoChartColors;
  valueAxisTitle: string;
  /** Sufixo de unidade no tooltip da barra (ex.: "h" ou "ocorr."). */
  unit: string;
  valueDecimals: number;
  locale: string;
  height?: number;
}

const ParetoChart: React.FC<ParetoChartProps> = ({
  labels,
  barValues,
  share,
  colors,
  valueAxisTitle,
  unit,
  valueDecimals,
  locale,
  height = 340,
}) => {
  const fmt = (v: number, dec: number): string =>
    Number(v).toLocaleString(locale, { minimumFractionDigits: dec, maximumFractionDigits: dec });

  const data = useMemo<ChartData>(
    () => ({
      labels,
      datasets: [
        {
          type: "bar" as const,
          label: valueAxisTitle,
          data: barValues,
          backgroundColor: colors.bars,
          borderRadius: 6,
          maxBarThickness: 96,
          categoryPercentage: 0.6,
          barPercentage: 0.9,
          yAxisID: "y",
        },
      ],
    }),
    [labels, barValues, colors, valueAxisTitle],
  );

  const options = useMemo<ChartOptions>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (item: any) => {
              const i = item.dataIndex;
              return `${valueAxisTitle}: ${fmt(Number(item.raw), valueDecimals)} ${unit} (${fmt(share[i] ?? 0, 1)}%)`;
            },
          },
        },
      },
      scales: {
        x: { grid: { display: false }, ticks: { color: colors.text, font: { size: 12 } } },
        y: {
          beginAtZero: true,
          position: "left",
          title: { display: true, text: valueAxisTitle, color: colors.text, font: { size: 11 } },
          ticks: { color: colors.text, font: { size: 11 } },
          grid: { color: colors.grid },
        },
      },
    }),
    [colors, valueAxisTitle, unit, valueDecimals, share, locale],
  );

  return (
    <div style={{ position: "relative", width: "100%", height }}>
      <Chart type="bar" data={data} options={options} />
    </div>
  );
};

export default ParetoChart;
